import { motion } from "framer-motion";
import { useState } from "react";
import { Sparkles, MapPin } from "lucide-react";
import { formatMXN } from "../lib/api";
import sitePlanImage from "../assets/site-plan.jpg";

const STATUS = {
  available: {
    bg: "#0B1A0F",
    ring: "#22c55e",
    text: "#B8E07A",
    label: "Disponible",
    glow: "rgba(34,197,94,0.9)",
  },
  reserved: {
    bg: "#0B1A0F",
    ring: "#F1C36B",
    text: "#F1C36B",
    label: "Reservado",
    glow: "rgba(241,195,107,0.9)",
  },
  sold: {
    bg: "#0B1A0F",
    ring: "#9CA3AF",
    text: "#D1D5DB",
    label: "Vendido",
    glow: "rgba(156,163,175,0.6)",
  },
};

/**
 * Lot positions overlaid on the actual survey image.
 * Coordinates are in % (image space, 0..100). Derived from visual analysis
 * of the 14 lot subdivisions on the survey.
 */
const DEFAULT_POSITIONS = [
  { x: 20, y: 18 },  // 1 — Upper-left (preventa)
  { x: 35, y: 15 },  // 2 — Upper, north of road loop (preventa)
  { x: 53, y: 14 },  // 3 — Upper-central, at road loop
  { x: 72, y: 17 },  // 4 — Upper-right
  { x: 85, y: 25 },  // 5 — Far east
  { x: 26, y: 38 },  // 6 — Middle-left
  { x: 48, y: 35 },  // 7 — Central, behind 3
  { x: 65, y: 33 },  // 8 — Central, east branch
  { x: 83, y: 40 },  // 9 — Middle-right
  { x: 28, y: 60 },  // 10 — Lower-left, common area edge
  { x: 48, y: 55 },  // 11 — Lower-central
  { x: 65, y: 53 },  // 12 — Lower-central east
  { x: 80, y: 60 },  // 13 — Lower-right
  { x: 45, y: 78 },  // 14 — Southernmost, common area
];

export default function SitePlan({ properties = [], onSelect, content = {} }) {
  const [hovered, setHovered] = useState(null);
  if (!properties.length) return null;

  const stats = {
    available: properties.filter((p) => (p.status || "available") === "available").length,
    reserved: properties.filter((p) => p.status === "reserved").length,
    sold: properties.filter((p) => p.status === "sold").length,
  };

  // Position each lot. If admin moved the pin (map_x/y != default 50,50) use that, else use layout.
  const positioned = properties.map((p, i) => {
    const fallback = DEFAULT_POSITIONS[(p.lot_number - 1) % 14] || DEFAULT_POSITIONS[i % 14];
    const isDefault = (p.map_x === 50 && p.map_y === 50) || p.map_x == null;
    return {
      ...p,
      x: isDefault ? fallback.x : p.map_x,
      y: isDefault ? fallback.y : p.map_y,
    };
  });

  return (
    <section
      id="mapa"
      data-testid="site-plan-section"
      className="relative py-28 overflow-hidden bg-[var(--c-bg-deep)]"
    >
      <div className="orb w-[460px] h-[460px] bg-[var(--c-bio-green)]/25 -top-32 -right-32" />
      <div className="orb w-[400px] h-[400px] bg-[var(--c-firefly-gold)]/25 bottom-0 -left-32" />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-medium text-[var(--c-firefly-gold)]">
            <MapPin className="w-4 h-4" /> {content.map_overline || "Plano oficial de la ecocomunidad"}
          </span>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl text-[var(--c-text)] leading-[1.05]">
            {content.map_title_1 || "Encuentra tu lugar en"}{" "}
            <span className="font-fairy text-[var(--c-bio-green)]">{content.map_title_fairy || "el mapa del cuento"}</span>
          </h2>
          <p className="mt-5 text-[var(--c-text)]/70 text-base">
            {content.map_description || "Plano oficial levantado · 15,000 m² delimitados como ecocomunidad. Pasa el cursor sobre cualquier lote para ver el detalle. Click para apartarlo."}
          </p>
        </motion.div>

        {/* Legend */}
        <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm" data-testid="map-legend">
          {Object.entries(STATUS).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-[var(--c-text)]/85">
              <span
                className="w-3.5 h-3.5 rounded-full border-2"
                style={{
                  background: v.bg,
                  borderColor: v.ring,
                  boxShadow: `0 0 14px ${v.glow}`,
                }}
              />
              {v.label} <span className="text-[var(--c-text)]/55">· {stats[k]}</span>
            </div>
          ))}
        </div>

        {/* Aerial map — centered, real aspect ratio of survey image (916x572) */}
        <div className="mt-12 mx-auto" style={{ maxWidth: "min(100%, 1100px)" }}>
          <div
            className="relative rounded-[2rem] overflow-hidden border-2 border-[var(--c-firefly-gold)]/30 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.7)] mx-auto"
            style={{ aspectRatio: "916 / 572", background: "#FAFAF6", containerType: "inline-size" }}
          >
            <img
              src={sitePlanImage}
              alt="Plano de la ecocomunidad Pextlán"
              className="absolute inset-0 w-full h-full object-contain select-none"
              draggable="false"
            />
            {/* Subtle gold vignette */}
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_80%_70%_at_50%_50%,transparent,rgba(11,26,15,0.12))]" />

            {/* Lot pins — dark filled circles with bright ring, high contrast on white map */}
            {positioned.map((p) => {
              const s = STATUS[p.status] || STATUS.available;
              return (
                <button
                  key={p.id}
                  data-testid={`site-plan-lot-${p.lot_number}`}
                  onClick={() => onSelect?.(p)}
                  onMouseEnter={() => setHovered(p)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(p)}
                  onBlur={() => setHovered(null)}
                  className="absolute -translate-x-1/2 -translate-y-1/2 group focus:outline-none"
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                  aria-label={`Lote ${p.lot_number} ${p.name}`}
                >
                  {/* Pulse */}
                  <span
                    className="absolute inset-0 rounded-full animate-ping"
                    style={{ background: s.ring, opacity: 0.35 }}
                  />
                  <span
                    className="relative block rounded-full flex items-center justify-center font-serif font-bold transition-transform duration-300 group-hover:scale-125 group-focus:scale-125"
                    style={{
                      width: "clamp(22px, 3.6cqw, 44px)",
                      height: "clamp(22px, 3.6cqw, 44px)",
                      fontSize: "clamp(10px, 1.5cqw, 18px)",
                      background: s.bg,
                      color: s.text,
                      border: `clamp(2px, 0.3cqw, 3px) solid ${s.ring}`,
                      boxShadow: `0 4px 16px rgba(0,0,0,0.45), 0 0 28px ${s.glow}`,
                    }}
                  >
                    {p.lot_number}
                  </span>
                  {p.is_presale && (
                    <span
                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white animate-pulse"
                      style={{ background: "#F1C36B", boxShadow: "0 0 12px rgba(241,195,107,0.9)" }}
                      title="Preventa"
                    />
                  )}
                </button>
              );
            })}

            {/* Hover tooltip — strong dark card with ring */}
            {hovered && (
              <div
                data-testid="map-tooltip"
                className="absolute pointer-events-none z-20 -translate-x-1/2 -translate-y-[calc(100%+20px)] rounded-2xl px-4 py-3 text-left shadow-[0_30px_70px_-15px_rgba(0,0,0,0.85)] min-w-[230px]"
                style={{
                  left: `${hovered.x}%`,
                  top: `${hovered.y}%`,
                  background: "rgba(11,26,15,0.97)",
                  backdropFilter: "blur(14px)",
                  border: "1px solid rgba(241,195,107,0.55)",
                  boxShadow:
                    "0 30px 70px -15px rgba(0,0,0,0.85), 0 0 28px rgba(184,224,122,0.25)",
                }}
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#F1C36B]">
                  <Sparkles className="w-3 h-3" /> Lote #{hovered.lot_number}
                </div>
                <div className="font-serif text-lg text-[#F2EFE6] leading-tight mt-0.5">
                  {hovered.name}
                </div>
                <div className="mt-1 flex items-center justify-between text-xs text-[#F2EFE6]/80">
                  <span>{hovered.surface?.toLocaleString()} m²</span>
                  <span className="text-[#B8E07A] font-semibold">{formatMXN(hovered.price)}</span>
                </div>
                <div
                  className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold"
                  style={{ color: (STATUS[hovered.status] || STATUS.available).ring }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: (STATUS[hovered.status] || STATUS.available).ring }}
                  />
                  {(STATUS[hovered.status] || STATUS.available).label}
                  {hovered.is_presale ? " · PREVENTA" : ""}
                </div>
              </div>
            )}

            {/* Bottom stamp */}
            <div className="absolute bottom-3 right-3 bg-[#0B1A0F]/90 backdrop-blur-md border border-[#F1C36B]/40 rounded-xl px-3 py-1.5 text-[11px] text-[#F1C36B] font-serif italic pointer-events-none">
              {content.map_stamp || "· plano oficial · 15,000 m² ·"}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
